// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Channels from '@app/features/channel/state/Channels';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {
	archiveColumn,
	createTask,
	deleteTask,
	loadGuildTasks,
	setArchived,
	toggleDone,
	updateTask,
} from '@app/features/tasks/commands/TaskCommands';
import {importTrelloBoard} from '@app/features/tasks/commands/TrelloImportCommands';
import {TaskEditDialog} from '@app/features/tasks/components/TaskEditDialog';
import styles from '@app/features/tasks/components/TasksView.module.css';
import Tasks, {
	assigneesOf,
	dueGroupOf,
	matchesTaskQuery,
	type Task,
	type TaskStatus,
} from '@app/features/tasks/state/Tasks';
import TasksTabState from '@app/features/tasks/state/TasksTab';
import {
	ADD_TODO_NAMED_DESCRIPTOR,
	ARCHIVE_COLUMN_CONFIRM_DESCRIPTOR,
	ARCHIVE_COLUMN_DESCRIPTOR,
	ARCHIVE_DESCRIPTOR,
	ARCHIVED_COUNT_DESCRIPTOR,
	ARCHIVED_DESCRIPTOR,
	DELETE_TODO_DESCRIPTOR,
	DOING_COLUMN_DESCRIPTOR,
	DONE_DESCRIPTOR,
	EVERYONE_DESCRIPTOR,
	IMPORT_TRELLO_DESCRIPTOR,
	IMPORTING_TRELLO_DESCRIPTOR,
	INPUT_COLUMN_DESCRIPTOR,
	MARK_DONE_DESCRIPTOR,
	MARK_NOT_DONE_DESCRIPTOR,
	MINE_DESCRIPTOR,
	MY_TODOS_DESCRIPTOR,
	NO_ARCHIVED_DESCRIPTOR,
	NO_DATE_DESCRIPTOR,
	NO_TODOS_DESCRIPTOR,
	NO_TODOS_MATCH_DESCRIPTOR,
	OVERDUE_DESCRIPTOR,
	PERSONAL_DESCRIPTOR,
	TEAM_DESCRIPTOR,
	QA_COLUMN_DESCRIPTOR,
	RESTORE_DESCRIPTOR,
	SEARCH_OR_ADD_TODO_DESCRIPTOR,
	TEAM_BOARD_DESCRIPTOR,
	TODAY_DESCRIPTOR,
	TODO_COLUMN_DESCRIPTOR,
	TRELLO_IMPORT_FAILED_DESCRIPTOR,
	TRELLO_IMPORTED_DESCRIPTOR,
	UPCOMING_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ArchiveIcon, ChatCircleIcon, MagnifyingGlassIcon, PlusIcon, XIcon} from '@phosphor-icons/react';
import clsx from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const BOARD_COLUMNS: ReadonlyArray<TaskStatus> = ['todo', 'input', 'doing', 'qa', 'done'];

function formatDue(due: string | null, locale: string): string {
	if (!due) return '';
	const [y, m, d] = due.split('-').map(Number);
	const date = new Date(y, m - 1, d);
	return date.toLocaleDateString(locale, {month: 'short', day: 'numeric'});
}

function openTask(task: Task): void {
	if (!task.channel_id) return;
	if (task.message_id) {
		RouterUtils.transitionTo(Routes.channelMessage(task.guild_id, task.channel_id, task.message_id));
		return;
	}
	RouterUtils.transitionTo(Routes.guildChannel(task.guild_id, task.channel_id));
}

const Highlight: React.FC<{text: string; query: string}> = ({text, query}) => {
	const q = query.trim();
	const index = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
	if (index < 0) return <>{text}</>;
	return (
		<>
			{text.slice(0, index)}
			<mark className={styles.mark} data-flx="tasks.tasks-view.highlight.mark">
				{text.slice(index, index + q.length)}
			</mark>
			{text.slice(index + q.length)}
		</>
	);
};

/** Up to three faces, then "+n". */
const AssigneeStack: React.FC<{task: Task}> = observer(({task}) => {
	const ids = assigneesOf(task);
	if (ids.length <= 1) return <AssigneeAvatar userId={ids[0] ?? null} data-flx="tasks.tasks-view.assignee-stack.single" />;
	const names = ids.map((id) => Users.getUser(id)?.username ?? '').filter(Boolean).join(', ');
	return (
		<span className={styles.avatarStack} title={names} data-flx="tasks.tasks-view.assignee-stack.stack">
			{ids.slice(0, 3).map((id) => {
				const user = Users.getUser(id);
				return user ? (
					<span key={id} className={styles.avatarStacked} data-flx="tasks.tasks-view.assignee-stack.avatar">
						<Avatar user={user} size={20} data-flx="tasks.tasks-view.assignee-stack.avatar-image" />
					</span>
				) : null;
			})}
			{ids.length > 3 && (
				<span className={styles.avatarMore} data-flx="tasks.tasks-view.assignee-stack.more">
					+{ids.length - 3}
				</span>
			)}
		</span>
	);
});

const AssigneeAvatar: React.FC<{userId: string | null}> = observer(({userId}) => {
	const user = userId ? Users.getUser(userId) : undefined;
	if (!user) return <span className={styles.avatarSpacer} data-flx="tasks.tasks-view.assignee-avatar.avatar-spacer" />;
	return (
		<span className={styles.avatar} title={user.username} data-flx="tasks.tasks-view.assignee-avatar.avatar">
			<Avatar user={user} size={20} data-flx="tasks.tasks-view.assignee-avatar.avatar-image" />
		</span>
	);
});

export const TaskRow: React.FC<{task: Task; query: string; showSource: boolean}> = observer(
	({task, query, showSource}) => {
		const {i18n} = useLingui();
		const done = task.status === 'done';
		const channel = task.channel_id ? Channels.getChannel(task.channel_id) : undefined;
		const overdue = !done && dueGroupOf(task) === 'overdue';
		const [editing, setEditing] = useState(false);
		return (
			<div className={clsx(styles.row, done && styles.rowDone)} data-flx="tasks.tasks-view.task-row.row">
				{editing && (
					<TaskEditDialog task={task} onClose={() => setEditing(false)} data-flx="tasks.tasks-view.task-row.edit-dialog" />
				)}
				<button
					type="button"
					className={clsx(styles.check, done && styles.checkDone)}
					aria-label={i18n._(done ? MARK_NOT_DONE_DESCRIPTOR : MARK_DONE_DESCRIPTOR, {title: task.title})}
					onClick={() => void toggleDone(task)}
					data-flx="tasks.tasks-view.task-row.check"
				/>
				<button
					type="button"
					className={styles.title}
					onClick={() => setEditing(true)}
					data-flx="tasks.tasks-view.task-row.title"
				>
					<Highlight text={task.title} query={query} data-flx="tasks.tasks-view.task-row.highlight" />
				</button>
				{showSource && (
					<span className={styles.meta} data-flx="tasks.tasks-view.task-row.source">
						{channel?.name ?? i18n._(task.private ? PERSONAL_DESCRIPTOR : TEAM_DESCRIPTOR)}
					</span>
				)}
				<AssigneeStack task={task} data-flx="tasks.tasks-view.task-row.assignee-avatar" />
				<label className={clsx(styles.due, overdue && styles.dueLate)} data-flx="tasks.tasks-view.task-row.due">
					<span data-flx="tasks.tasks-view.task-row.due-text">{formatDue(task.due_date, i18n.locale)}</span>
					<input
						type="date"
						className={styles.dueInput}
						value={task.due_date ?? ''}
						onChange={(event) => void updateTask(task, {due_date: event.target.value || null})}
						data-flx="tasks.tasks-view.task-row.due-input"
					/>
				</label>
				<button
					type="button"
					className={styles.remove}
					aria-label={i18n._(DELETE_TODO_DESCRIPTOR)}
					onClick={() => void deleteTask(task)}
					data-flx="tasks.tasks-view.task-row.remove"
				>
					<XIcon size={14} data-flx="tasks.tasks-view.task-row.remove-icon" />
				</button>
			</div>
		);
	},
);

const MyTodos: React.FC<{guildId: string; query: string}> = observer(({guildId, query}) => {
	const {i18n} = useLingui();
	const me = Users.currentUserId;
	const mine = me ? Tasks.getMyTasks(guildId, me) : [];
	const open = mine.filter((task) => task.status !== 'done' && matchesTaskQuery(task, query));
	const done = mine.filter((task) => task.status === 'done' && matchesTaskQuery(task, query));
	const [showDone, setShowDone] = useState(false);
	const dueGroups = (tasks: Array<Task>): Array<[string, Array<Task>, boolean]> => [
		[i18n._(OVERDUE_DESCRIPTOR), tasks.filter((t) => dueGroupOf(t) === 'overdue'), true],
		[i18n._(TODAY_DESCRIPTOR), tasks.filter((t) => dueGroupOf(t) === 'today'), false],
		[i18n._(UPCOMING_DESCRIPTOR), tasks.filter((t) => dueGroupOf(t) === 'upcoming'), false],
		[i18n._(NO_DATE_DESCRIPTOR), tasks.filter((t) => dueGroupOf(t) === 'none'), false],
	];
	// Personal to-dos first (only you see them), then team to-dos you're on.
	const blocks: Array<[string, string, Array<[string, Array<Task>, boolean]>]> = [
		['personal', i18n._(PERSONAL_DESCRIPTOR), dueGroups(open.filter((t) => t.private))],
		['team', i18n._(TEAM_DESCRIPTOR), dueGroups(open.filter((t) => !t.private))],
	];
	if (open.length === 0 && done.length === 0) {
		return (
			<p className={styles.empty} data-flx="tasks.tasks-view.my-todos.empty">
				{query.trim() ? i18n._(NO_TODOS_MATCH_DESCRIPTOR, {query}) : i18n._(NO_TODOS_DESCRIPTOR)}
			</p>
		);
	}
	return (
		<div className={styles.list} data-flx="tasks.tasks-view.my-todos.list">
			{blocks.map(([key, blockLabel, groups]) =>
				groups.every(([, rows]) => rows.length === 0) ? null : (
					<div key={key} className={styles.block} data-flx="tasks.tasks-view.my-todos.block">
						<h2 className={styles.blockLabel} data-flx="tasks.tasks-view.my-todos.block-label">
							{blockLabel}
						</h2>
						{groups.map(([label, rows, late]) =>
							rows.length === 0 ? null : (
								<section key={label} data-flx="tasks.tasks-view.my-todos.group">
									<h3
										className={clsx(styles.group, late && styles.groupLate)}
										data-flx="tasks.tasks-view.my-todos.group-label"
									>
										{label}
									</h3>
									{rows.map((task) => (
										<TaskRow
											key={task.id}
											task={task}
											query={query}
											showSource={key === 'team'}
											data-flx="tasks.tasks-view.my-todos.task-row"
										/>
									))}
								</section>
							),
						)}
					</div>
				),
			)}
			{done.length > 0 && (
				<section data-flx="tasks.tasks-view.my-todos.done-group">
					<button
						type="button"
						className={styles.group}
						onClick={() => setShowDone((v) => !v)}
						data-flx="tasks.tasks-view.my-todos.done-toggle"
					>
						{i18n._(DONE_DESCRIPTOR)} · {done.length}
					</button>
					{showDone &&
						done.map((task) => (
							<TaskRow
								key={task.id}
								task={task}
								query={query}
								showSource
								data-flx="tasks.tasks-view.my-todos.done-row"
							/>
						))}
				</section>
			)}
		</div>
	);
});

const ADD_TODO_IN_COLUMN_DESCRIPTOR = msg({
	message: 'Add a to-do',
	comment: 'Button at the bottom of a team board column that adds a to-do to that column.',
});
const NEW_TODO_PLACEHOLDER_DESCRIPTOR = msg({
	message: 'What needs doing?',
	comment: 'Placeholder of the new to-do box at the bottom of a team board column.',
});

/** "Add a to-do" at the bottom of a board column; Enter adds a team to-do in that column. */
const ColumnAdd: React.FC<{guildId: string; status: TaskStatus}> = observer(({guildId, status}) => {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);
	const [text, setText] = useState('');
	const [busy, setBusy] = useState(false);
	const close = () => {
		setOpen(false);
		setText('');
	};
	const add = async () => {
		const title = text.trim();
		if (!title || busy) return;
		setBusy(true);
		const created = await createTask(guildId, {title: title.charAt(0).toUpperCase() + title.slice(1), status});
		setBusy(false);
		if (created) setText('');
	};
	if (!open) {
		return (
			<button
				type="button"
				className={styles.columnAdd}
				onClick={() => setOpen(true)}
				data-flx="tasks.tasks-view.column-add.open.button"
			>
				<PlusIcon size={14} aria-hidden="true" data-flx="tasks.tasks-view.column-add.plus-icon" />
				{i18n._(ADD_TODO_IN_COLUMN_DESCRIPTOR)}
			</button>
		);
	}
	return (
		<div className={styles.columnAddForm} data-flx="tasks.tasks-view.column-add.form">
			<textarea
				// biome-ignore lint/a11y/noAutofocus: opened on purpose by the Add a to-do button
				autoFocus
				className={styles.columnAddInput}
				rows={2}
				maxLength={300}
				value={text}
				placeholder={i18n._(NEW_TODO_PLACEHOLDER_DESCRIPTOR)}
				aria-label={i18n._(ADD_TODO_IN_COLUMN_DESCRIPTOR)}
				onChange={(event) => setText(event.target.value)}
				onKeyDown={(event) => {
					if (event.key === 'Enter' && !event.shiftKey) {
						event.preventDefault();
						void add();
					} else if (event.key === 'Escape') {
						close();
					}
				}}
				onBlur={() => {
					if (!text.trim()) close();
				}}
				data-flx="tasks.tasks-view.column-add.input"
			/>
		</div>
	);
});

const TeamBoard: React.FC<{guildId: string; query: string; onlyMine: boolean}> = observer(
	({guildId, query, onlyMine}) => {
		const {i18n} = useLingui();
		const me = Users.currentUserId;
		const [dragId, setDragId] = useState<string | null>(null);
		// Read the dragged id from a ref: changing the dragged card's DOM during dragstart cancels the drag in Chrome.
		const dragIdRef = useRef<string | null>(null);
		const [overColumn, setOverColumn] = useState<TaskStatus | null>(null);
		const [editingId, setEditingId] = useState<string | null>(null);
		const all = Tasks.getGuildTasks(guildId).filter(
			(task) =>
				!task.private &&
				matchesTaskQuery(task, query) &&
				(!onlyMine || (me != null && assigneesOf(task).includes(me))),
		);
		const editing = editingId ? all.find((task) => task.id === editingId) : undefined;
		const labels: Record<TaskStatus, string> = {
			todo: i18n._(TODO_COLUMN_DESCRIPTOR),
			input: i18n._(INPUT_COLUMN_DESCRIPTOR),
			doing: i18n._(DOING_COLUMN_DESCRIPTOR),
			qa: i18n._(QA_COLUMN_DESCRIPTOR),
			done: i18n._(DONE_DESCRIPTOR),
		};
		const archiveAll = async (status: TaskStatus, count: number) => {
			if (!window.confirm(i18n._(ARCHIVE_COLUMN_CONFIRM_DESCRIPTOR, {count, column: labels[status]}))) return;
			const archived = await archiveColumn(guildId, status);
			ToastCommands.success(i18n._(ARCHIVED_COUNT_DESCRIPTOR, {count: archived}));
		};
		const moveTo = (task: Task, status: TaskStatus) => {
			if (task.status !== status) void updateTask(task, {status});
		};
		const handleCardKey = (event: React.KeyboardEvent, task: Task) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				setEditingId(task.id);
				return;
			}
			if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
			event.preventDefault();
			const index = BOARD_COLUMNS.indexOf(task.status) + (event.key === 'ArrowRight' ? 1 : -1);
			if (index >= 0 && index < BOARD_COLUMNS.length) moveTo(task, BOARD_COLUMNS[index]);
		};
		return (
			<div className={styles.board} data-flx="tasks.tasks-view.team-board.board">
				{editing && (
					<TaskEditDialog
						task={editing}
						onClose={() => setEditingId(null)}
						data-flx="tasks.tasks-view.team-board.edit-dialog"
					/>
				)}
				{BOARD_COLUMNS.map((status) => {
					const cards = all.filter((task) => task.status === status);
					return (
						// biome-ignore lint/a11y/noStaticElementInteractions: drop target for dragging cards; cards also move with the arrow keys
						<section
							key={status}
							className={clsx(styles.column, overColumn === status && styles.columnOver)}
							onDragOver={(event) => {
								if (!dragIdRef.current) return;
								event.preventDefault();
								setOverColumn(status);
							}}
							onDragLeave={() => setOverColumn((current) => (current === status ? null : current))}
							onDrop={(event) => {
								event.preventDefault();
								const id = dragIdRef.current ?? event.dataTransfer.getData('text/plain');
								const task = all.find((t) => t.id === id);
								if (task) moveTo(task, status);
								dragIdRef.current = null;
								setDragId(null);
								setOverColumn(null);
							}}
							data-flx="tasks.tasks-view.team-board.column"
						>
							<h3 className={styles.columnHead} data-flx="tasks.tasks-view.team-board.column-head">
								<span data-flx="tasks.tasks-view.team-board.column-label">{labels[status]}</span>
								<span className={styles.columnRight} data-flx="tasks.tasks-view.team-board.column-right">
									{cards.length > 0 && (
										<button
											type="button"
											className={styles.columnAction}
											aria-label={`${i18n._(ARCHIVE_COLUMN_DESCRIPTOR)}: ${labels[status]}`}
											title={i18n._(ARCHIVE_COLUMN_DESCRIPTOR)}
											onClick={() => void archiveAll(status, cards.length)}
											data-flx="tasks.tasks-view.team-board.column-action.button"
										>
											<ArchiveIcon size={14} aria-hidden="true" data-flx="tasks.tasks-view.team-board.column-archive-icon" />
										</button>
									)}
									<span data-flx="tasks.tasks-view.team-board.column-count">{cards.length}</span>
								</span>
							</h3>
							<div className={styles.cards} data-flx="tasks.tasks-view.team-board.cards">
								{cards.map((task) => {
									const channel = task.channel_id ? Channels.getChannel(task.channel_id) : undefined;
									const overdue = task.status !== 'done' && dueGroupOf(task) === 'overdue';
									return (
										<div
											key={task.id}
											role="button"
											tabIndex={0}
											draggable
											className={clsx(
												styles.card,
												dragId === task.id && styles.cardDragging,
												task.status === 'done' && styles.cardDone,
											)}
											onDragStart={(event) => {
												dragIdRef.current = task.id;
												event.dataTransfer.effectAllowed = 'move';
												event.dataTransfer.setData('text/plain', task.id);
												window.setTimeout(() => setDragId(task.id), 0);
											}}
											onDragEnd={() => {
												dragIdRef.current = null;
												setDragId(null);
												setOverColumn(null);
											}}
											onKeyDown={(event) => handleCardKey(event, task)}
											onClick={() => setEditingId(task.id)}
											data-flx="tasks.tasks-view.team-board.card"
										>
											<span className={styles.cardTitle} data-flx="tasks.tasks-view.team-board.card-title">
												<Highlight text={task.title} query={query} data-flx="tasks.tasks-view.team-board.highlight" />
											</span>
											<span className={styles.cardFoot} data-flx="tasks.tasks-view.team-board.card-foot">
												<button
													type="button"
													className={styles.meta}
													onClick={(event) => {
														event.stopPropagation();
														openTask(task);
													}}
													disabled={!task.channel_id}
													data-flx="tasks.tasks-view.team-board.card-source"
												>
													{channel ? `# ${channel.name}` : i18n._(TEAM_DESCRIPTOR)}
												</button>
												<span className={styles.cardRight} data-flx="tasks.tasks-view.team-board.card-right">
													<span
														className={clsx(styles.meta, overdue && styles.dueLate)}
														data-flx="tasks.tasks-view.team-board.card-due"
													>
														{formatDue(task.due_date, i18n.locale)}
													</span>
													{(task.comments?.length ?? 0) > 0 && (
														<span className={styles.commentCount} data-flx="tasks.tasks-view.team-board.comment-count">
															<ChatCircleIcon size={13} aria-hidden="true" data-flx="tasks.tasks-view.team-board.comment-icon" />
															{task.comments?.length}
														</span>
													)}
													<AssigneeStack task={task} data-flx="tasks.tasks-view.team-board.card-avatar" />
													<button
														type="button"
														className={styles.cardArchive}
														aria-label={`${i18n._(ARCHIVE_DESCRIPTOR)}: ${task.title}`}
														title={i18n._(ARCHIVE_DESCRIPTOR)}
														onClick={(event) => {
															event.stopPropagation();
															void setArchived(task, true);
														}}
														data-flx="tasks.tasks-view.team-board.card-archive.stop-propagation.button"
													>
														<ArchiveIcon
															size={14}
															aria-hidden="true"
															data-flx="tasks.tasks-view.team-board.archive-icon"
														/>
													</button>
												</span>
											</span>
										</div>
									);
								})}
							</div>
							<ColumnAdd guildId={guildId} status={status} data-flx="tasks.tasks-view.team-board.column-add" />
						</section>
					);
				})}
			</div>
		);
	},
);

const ArchivedList: React.FC<{guildId: string; query: string}> = observer(({guildId, query}) => {
	const {i18n} = useLingui();
	const archived = Tasks.getArchivedTasks(guildId).filter((task) => matchesTaskQuery(task, query));
	if (archived.length === 0)
		return (
			<p className={styles.empty} data-flx="tasks.tasks-view.archived-list.empty">
				{i18n._(NO_ARCHIVED_DESCRIPTOR)}
			</p>
		);
	return (
		<div className={styles.list} data-flx="tasks.tasks-view.archived-list.list">
			{archived.map((task) => (
				<div
					key={task.id}
					className={clsx(styles.row, styles.archivedRow)}
					data-flx="tasks.tasks-view.archived-list.row"
				>
					<span className={styles.title} data-flx="tasks.tasks-view.archived-list.title">
						<Highlight text={task.title} query={query} data-flx="tasks.tasks-view.archived-list.highlight" />
					</span>
					<span className={styles.meta} data-flx="tasks.tasks-view.archived-list.meta">
						{formatDue(task.archived_at?.slice(0, 10) ?? null, i18n.locale)}
					</span>
					<AssigneeAvatar userId={task.assignee_id} data-flx="tasks.tasks-view.archived-list.assignee-avatar" />
					<button
						type="button"
						className={styles.filterBtn}
						onClick={() => void setArchived(task, false)}
						data-flx="tasks.tasks-view.archived-list.filter-btn.button"
					>
						{i18n._(RESTORE_DESCRIPTOR)}
					</button>
				</div>
			))}
		</div>
	);
});

function TrelloImportButton({guildId}: {guildId: string}) {
	const {i18n} = useLingui();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [progress, setProgress] = useState<{done: number; total: number} | null>(null);
	const onFile = useCallback(
		async (file: File | undefined) => {
			if (!file) return;
			try {
				const result = await importTrelloBoard(guildId, file, (done, total) => setProgress({done, total}));
				ToastCommands.success(i18n._(TRELLO_IMPORTED_DESCRIPTOR, {added: result.added, skipped: result.skipped}));
			} catch {
				ToastCommands.error(i18n._(TRELLO_IMPORT_FAILED_DESCRIPTOR));
			} finally {
				setProgress(null);
				if (inputRef.current) inputRef.current.value = '';
			}
		},
		[guildId, i18n],
	);
	return (
		<>
			<button
				type="button"
				className={styles.filterBtn}
				disabled={progress != null}
				onClick={() => inputRef.current?.click()}
				data-flx="tasks.tasks-view.trello-import-button.filter-btn.click.button"
			>
				{progress
					? i18n._(IMPORTING_TRELLO_DESCRIPTOR, {done: progress.done, total: progress.total})
					: i18n._(IMPORT_TRELLO_DESCRIPTOR)}
			</button>
			<input
				ref={inputRef}
				type="file"
				accept="application/json,.json"
				hidden
				onChange={(event) => void onFile(event.target.files?.[0])}
				data-flx="tasks.tasks-view.trello-import-button.input.file"
			/>
		</>
	);
}

export const TasksView: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const tab = TasksTabState.tab;
	const setTab = TasksTabState.setTab;
	const [query, setQuery] = useState('');
	const [boardFilter, setBoardFilter] = useState<'everyone' | 'mine' | 'archived'>('everyone');
	useEffect(() => {
		void loadGuildTasks(guildId);
	}, [guildId]);
	const trimmed = query.trim();
	const exactExists = useMemo(
		() => Tasks.getGuildTasks(guildId).some((task) => task.title.toLowerCase() === trimmed.toLowerCase()),
		[guildId, trimmed, Tasks.getGuildTasks(guildId).length],
	);
	const add = useCallback(async () => {
		if (!trimmed) return;
		const title = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
		// Typed into My to-dos: a personal to-do. Typed on the team board: a team to-do.
		const created = await createTask(guildId, {title, private: tab === 'mine'});
		if (created) setQuery('');
	}, [guildId, trimmed, tab]);
	return (
		<div className={styles.root} data-flx="tasks.tasks-view.root">
			<div className={styles.toolbar} data-flx="tasks.tasks-view.toolbar">
				<div
					className={clsx(styles.tabs, MobileLayout.enabled && styles.segmented)}
					role="tablist"
					data-flx="tasks.tasks-view.tabs"
				>
					<button
						type="button"
						role="tab"
						aria-selected={tab === 'mine'}
						className={clsx(styles.tab, tab === 'mine' && styles.tabOn)}
						onClick={() => setTab('mine')}
						data-flx="tasks.tasks-view.tab-mine"
					>
						{i18n._(MY_TODOS_DESCRIPTOR)}
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={tab === 'board'}
						className={clsx(styles.tab, tab === 'board' && styles.tabOn)}
						onClick={() => setTab('board')}
						data-flx="tasks.tasks-view.tab-board"
					>
						{i18n._(TEAM_BOARD_DESCRIPTOR)}
					</button>
				</div>
				{tab === 'board' && !MobileLayout.enabled && (
					<div className={styles.filter} data-flx="tasks.tasks-view.filter">
						<TrelloImportButton guildId={guildId} data-flx="tasks.tasks-view.trello-import-button" />
						<button
							type="button"
							className={clsx(styles.filterBtn, boardFilter === 'everyone' && styles.filterOn)}
							onClick={() => setBoardFilter('everyone')}
							data-flx="tasks.tasks-view.filter-everyone"
						>
							{i18n._(EVERYONE_DESCRIPTOR)}
						</button>
						<button
							type="button"
							className={clsx(styles.filterBtn, boardFilter === 'mine' && styles.filterOn)}
							onClick={() => setBoardFilter('mine')}
							data-flx="tasks.tasks-view.filter-mine"
						>
							{i18n._(MINE_DESCRIPTOR)}
						</button>
						<button
							type="button"
							className={clsx(styles.filterBtn, boardFilter === 'archived' && styles.filterOn)}
							onClick={() => setBoardFilter('archived')}
							data-flx="tasks.tasks-view.filter-btn.set-board-filter.button"
						>
							{i18n._(ARCHIVED_DESCRIPTOR)}
						</button>
					</div>
				)}
			</div>
			<div className={styles.field} data-flx="tasks.tasks-view.field">
				<MagnifyingGlassIcon size={16} className={styles.fieldIcon} data-flx="tasks.tasks-view.field-icon" />
				<input
					type="text"
					className={styles.input}
					value={query}
					placeholder={i18n._(SEARCH_OR_ADD_TODO_DESCRIPTOR)}
					aria-label={i18n._(SEARCH_OR_ADD_TODO_DESCRIPTOR)}
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							event.preventDefault();
							void add();
						} else if (event.key === 'Escape') {
							setQuery('');
						}
					}}
					data-flx="tasks.tasks-view.input"
				/>
				<kbd className={styles.hint} data-flx="tasks.tasks-view.hint">
					Enter
				</kbd>
			</div>
			{trimmed && !exactExists && (
				<button type="button" className={styles.addRow} onClick={() => void add()} data-flx="tasks.tasks-view.add-row">
					<PlusIcon size={16} data-flx="tasks.tasks-view.add-row-icon" />
					<span data-flx="tasks.tasks-view.add-row-label">{i18n._(ADD_TODO_NAMED_DESCRIPTOR, {title: trimmed})}</span>
				</button>
			)}
			{tab === 'mine' && <MyTodos guildId={guildId} query={query} data-flx="tasks.tasks-view.my-todos" />}
			{tab === 'board' && boardFilter === 'archived' && (
				<ArchivedList guildId={guildId} query={query} data-flx="tasks.tasks-view.archived-list" />
			)}
			{tab === 'board' && boardFilter !== 'archived' && (
				<TeamBoard
					guildId={guildId}
					query={query}
					onlyMine={boardFilter === 'mine'}
					data-flx="tasks.tasks-view.team-board"
				/>
			)}
		</div>
	);
});
